
import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function MaterialSelector({
  materialType,
  value = {},
  onSelect,
  onProcessSelect,
  processType,
  disabled,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // RESET WHEN MATERIAL TYPE CHANGES
  useEffect(() => {
    setOptions([]);
    setSearchText("");
    setIsOpen(false);
  }, [materialType]);

  // LOAD MATERIAL LIST
  useEffect(() => {
    if (!materialType) return;

    let cancelled = false;

    const loadMaterials = async () => {
      setLoading(true);

      try {
        let list = [];

        if (materialType === "RAW") {
          // Fetch the first page to determine total pages.
          const firstResponse = await axiosClient.get(
            "/api/ppe/masters/raw-materials",
            {
              params: {
                page: 1,
                per_page: 1000,
              },
            }
          );

          const firstData = firstResponse.data?.data;
          const firstPageList = Array.isArray(firstData?.data)
            ? firstData.data
            : Array.isArray(firstData)
            ? firstData
            : [];

          list = [...firstPageList];

          const totalPages = Number(
            firstData?.pagination?.pages ??
              firstResponse.data?.pagination?.pages ??
              1
          );

          // Fetch remaining pages.
          if (totalPages > 1) {
            const remainingPages = [];

            for (let page = 2; page <= totalPages; page++) {
              remainingPages.push(
                axiosClient.get("/api/ppe/masters/raw-materials", {
                  params: {
                    page,
                    per_page: 1000,
                  },
                })
              );
            }

            const responses = await Promise.all(remainingPages);

            responses.forEach((res) => {
              const responseData = res.data?.data;

              const pageList = Array.isArray(responseData?.data)
                ? responseData.data
                : Array.isArray(responseData)
                ? responseData
                : [];

              list.push(...pageList);
            });
          }
        } else if (materialType === "BOUGHT_OUT") {
          const res = await axiosClient.get(
            "/api/ppe/masters/boughtout-items"
          );

          const responseData = res.data?.data;

          list = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
            ? responseData.data
            : [];
        } else if (materialType === "SUB_PART") {
          const res = await axiosClient.get(
            "/api/ppe/masters/product-variants"
          );

          const responseData = res.data?.data;

          list = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
            ? responseData.data
            : [];
        }

        if (cancelled) return;

        // Preserve the existing RAW engineering material filter.
        const filteredList =
          materialType === "RAW"
            ? list.filter(
                (item) =>
                  String(item.sku_type || "")
                    .trim()
                    .toUpperCase() === "ENG"
              )
            : list;

        setOptions(filteredList);
      } catch (error) {
        console.error("Failed to load materials:", error);

        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMaterials();

    return () => {
      cancelled = true;
    };
  }, [materialType]);

  // CURRENT SELECTED ID
  const getCurrentValue = () => {
    if (materialType === "RAW") {
      return value.raw_material_id
        ? String(value.raw_material_id)
        : "";
    }

    if (materialType === "BOUGHT_OUT") {
      return value.bought_out_item_id
        ? String(value.bought_out_item_id)
        : "";
    }

    if (materialType === "SUB_PART") {
      return value.sub_part_variant_id
        ? String(value.sub_part_variant_id)
        : "";
    }

    return "";
  };

  const selectedValue = getCurrentValue();

  // MATERIAL CODE AND NAME
  const getCode = (item) =>
    String(
      item.item_code ||
        item.material_code ||
        item.raw_material_code ||
        item.code ||
        item.variant_code ||
        item.rm_code ||
        ""
    );

  const getName = (item) =>
    String(
      item.item_name ||
        item.material_name ||
        item.raw_material_name ||
        item.variant_name ||
        item.name ||
        item.rm_name ||
        ""
    );

  const getLabel = (item) => {
    const code = getCode(item);
    const name = getName(item);

    if (code && name) return `${code} - ${name}`;

    return code || name || "";
  };

  const safeOptions = Array.isArray(options) ? options : [];

  const selectedOption = safeOptions.find(
    (item) => String(item.id) === selectedValue
  );

  // Show the saved material label when loading/editing a BOQ.
  useEffect(() => {
    if (!isOpen) {
      setSearchText(selectedOption ? getLabel(selectedOption) : "");
    }
  }, [selectedValue, selectedOption, isOpen]);

  // SEARCH BY MATERIAL CODE OR NAME
  const normalizedSearch = searchText.trim().toLowerCase();

  const filteredOptions = safeOptions.filter((item) => {
    if (!normalizedSearch) return true;

    const code = getCode(item).toLowerCase();
    const name = getName(item).toLowerCase();

    return (
      code.includes(normalizedSearch) ||
      name.includes(normalizedSearch) ||
      getLabel(item).toLowerCase().includes(normalizedSearch)
    );
  });

  // HANDLE MATERIAL SELECTION
  const handleChange = (selectedId) => {
    if (!selectedId) return;

    const id = Number(selectedId);

    const payload = {
      raw_material_id: null,
      bought_out_item_id: null,
      sub_part_variant_id: null,
    };

    const selectedMaterial = options.find(
      (item) => Number(item.id) === id
    );

    if (materialType === "RAW") {
      payload.raw_material_id = id;

      payload.uom_id =
        selectedMaterial?.issue_uom_id ??
        selectedMaterial?.base_uom_id ??
        null;

      payload.uom_code =
        selectedMaterial?.issue_uom_code ??
        selectedMaterial?.base_uom_code ??
        selectedMaterial?.issue_uom_name ??
        selectedMaterial?.base_uom_name ??
        selectedMaterial?.uom ??
        "";
    }

    if (materialType === "BOUGHT_OUT") {
      payload.bought_out_item_id = id;

      payload.uom_id =
        selectedMaterial?.issue_uom_id ??
        selectedMaterial?.base_uom_id ??
        null;

      payload.uom_code =
        selectedMaterial?.uom_code ??
        selectedMaterial?.uom_name ??
        selectedMaterial?.issue_uom_name ??
        selectedMaterial?.base_uom_name ??
        "";
    }

    if (materialType === "SUB_PART") {
      payload.sub_part_variant_id = id;
      payload.uom_id = selectedMaterial?.uom_id ?? null;

      payload.uom_code =
        selectedMaterial?.uom_code ??
        selectedMaterial?.uom_name ??
        "";
    }

    const selected = safeOptions.find(
      (item) => String(item.id) === String(selectedId)
    );

    setSearchText(selected ? getLabel(selected) : "");
    setIsOpen(false);

    if (typeof onSelect === "function") {
      onSelect(payload);
    }

    if (typeof onProcessSelect === "function") {
      onProcessSelect({
        id,
        processType,
      });
    }
  };

  // SEARCHABLE DROPDOWN
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minWidth: 0,
      }}
    >
      <input
        type="text"
        className="form-select form-select-sm"
        value={searchText}
        disabled={disabled || loading}
        placeholder={
          loading
            ? "Loading materials..."
            : "Search by material code or name..."
        }
        autoComplete="off"
        onFocus={() => {
          setIsOpen(true);
          setSearchText("");
        }}
        onChange={(e) => {
          setSearchText(e.target.value);
          setIsOpen(true);
        }}
        onBlur={() => {
          // Allow an option to be clicked before closing.
          setTimeout(() => {
            setIsOpen(false);

            const current = safeOptions.find(
              (item) => String(item.id) === selectedValue
            );

            setSearchText(current ? getLabel(current) : "");
          }, 150);
        }}
        aria-label="Search material by code or name"
        aria-expanded={isOpen}
        role="combobox"
        aria-autocomplete="list"
      />

      {isOpen && !disabled && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: 260,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #ced4da",
            borderRadius: 4,
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          }}
        >
          {filteredOptions.length === 0 ? (
            <div
              className="text-muted"
              style={{ padding: "8px 10px", fontSize: 13 }}
            >
              {loading
                ? "Loading materials..."
                : "No matching materials found"}
            </div>
          ) : (
            filteredOptions.map((item) => (
              <div
                key={item.id}
                role="option"
                aria-selected={String(item.id) === selectedValue}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleChange(String(item.id));
                }}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 13,
                  borderBottom: "1px solid #f1f1f1",
                  background:
                    String(item.id) === selectedValue
                      ? "#e7f1ff"
                      : "#fff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f0f7ff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    String(item.id) === selectedValue
                      ? "#e7f1ff"
                      : "#fff";
                }}
              >
                {getLabel(item)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
